import React from 'react';
import { RefreshCw, Trash2, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { usePersonAuditLogs } from '../hooks/useDeviceAuditLogs';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../utils/cn';

interface PersonAuditLogTableProps {
  customerId: string;
  personId: string;
  darkMode?: boolean;
  onViewAll?: () => void;
}

const getOperationLabel = (type: string): string => ({
  sync:                 'Sincronização',
  manual_sync:          'Sincronização Manual',
  sync_photo:           'Foto sincronizada',
  remove:               'Remoção',
  manual_revoke:        'Revogação Manual',
  auto_revoke_entrada:  'Revogação Auto. (Entrada)',
  auto_revoke_saida:    'Revogação Auto. (Saída)',
}[type] ?? type);

const isRevoke = (type: string) =>
  type === 'remove' || type === 'manual_revoke' || type.startsWith('auto_revoke');

const formatDuration = (ms?: number): string => {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

export const PersonAuditLogTable: React.FC<PersonAuditLogTableProps> = ({
  customerId,
  personId,
  darkMode = false,
  onViewAll,
}) => {
  const { logs, loading, error } = usePersonAuditLogs(customerId, personId);

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-6 text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>
        <RefreshCw className="w-3.5 h-3.5 animate-spin mr-2" />
        Carregando histórico...
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("flex items-center gap-2 py-4 text-xs", darkMode ? "text-red-400" : "text-red-500")}>
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
        {error}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <p className={cn("text-xs text-center py-6", darkMode ? "text-gray-500" : "text-gray-400")}>
        Nenhum histórico de sincronização disponível
      </p>
    );
  }

  const visible = logs.slice(0, 15);

  return (
    <div className={cn("rounded-lg border overflow-hidden", darkMode ? "border-gray-700" : "border-gray-200")}>
      {/* Column headers */}
      <div
        className={cn(
          "grid px-3 py-2 text-[10px] font-semibold uppercase tracking-widest select-none",
          darkMode ? "bg-gray-800/60 text-gray-500 border-b border-gray-700" : "bg-gray-50 text-gray-400 border-b border-gray-200"
        )}
        style={{ gridTemplateColumns: 'auto 1fr auto auto auto' }}
      >
        <span className="w-24">Data/Hora</span>
        <span>Operação</span>
        <span className="w-36">Dispositivo</span>
        <span className="w-20 text-center">Status</span>
        <span className="w-14 text-right">Duração</span>
      </div>

      {/* Rows */}
      {visible.map((log, idx) => {
        const revoke = isRevoke(log.operation_type);
        const success = log.status === 'success';
        const failed = log.status === 'failed';

        return (
          <div
            key={log.id}
            className={cn(
              "grid items-center px-3 py-2.5 gap-3 text-xs",
              idx > 0 && (darkMode ? "border-t border-gray-800" : "border-t border-gray-100"),
              darkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50/60"
            )}
            style={{ gridTemplateColumns: 'auto 1fr auto auto auto' }}
          >
            {/* Time */}
            <div className="w-24 relative group">
              <span className={cn("cursor-default", darkMode ? "text-gray-400" : "text-gray-500")}>
                {formatDistanceToNow(new Date(log.performed_at), { addSuffix: true, locale: ptBR })}
              </span>
              {/* Tooltip */}
              <span className={cn(
                "absolute left-0 bottom-full mb-1.5 z-50 px-2 py-1 rounded text-[10px] whitespace-nowrap pointer-events-none",
                "opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg",
                darkMode ? "bg-gray-700 text-gray-100 border border-gray-600" : "bg-gray-900 text-white"
              )}>
                {new Date(log.performed_at).toLocaleString('pt-BR')}
              </span>
            </div>

            {/* Operation */}
            <div className="flex items-center gap-2 min-w-0">
              {revoke
                ? <Trash2 className={cn("w-3.5 h-3.5 flex-shrink-0", darkMode ? "text-red-400" : "text-red-400")} />
                : <RefreshCw className={cn("w-3.5 h-3.5 flex-shrink-0", darkMode ? "text-blue-400" : "text-blue-500")} />
              }
              <span className={cn("truncate font-medium", darkMode ? "text-gray-200" : "text-gray-700")}>
                {getOperationLabel(log.operation_type)}
              </span>
            </div>

            {/* Device + Zone */}
            <div className="w-36 min-w-0">
              <p className={cn("truncate", darkMode ? "text-gray-300" : "text-gray-700")}>
                {log.device?.name || '—'}
              </p>
              {log.zone?.name && (
                <p className={cn("truncate text-[10px] mt-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>
                  {log.zone.name}
                </p>
              )}
            </div>

            {/* Status */}
            <div className="w-20 flex justify-center">
              {success ? (
                <span className={cn("flex items-center gap-1 font-medium", darkMode ? "text-green-400" : "text-green-600")}>
                  <CheckCircle className="w-3 h-3" />
                  Sucesso
                </span>
              ) : failed ? (
                <div className="relative group flex items-center gap-1">
                  <span className={cn("flex items-center gap-1 font-medium", darkMode ? "text-red-400" : "text-red-500")}>
                    <XCircle className="w-3 h-3" />
                    Falhou
                  </span>
                  {log.error_message && (
                    <span className={cn(
                      "absolute right-0 bottom-full mb-1.5 z-50 px-2 py-1 rounded text-[10px] w-48 leading-snug pointer-events-none",
                      "opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg",
                      darkMode ? "bg-gray-700 text-gray-100 border border-gray-600" : "bg-gray-900 text-white"
                    )}>
                      {log.error_message}
                    </span>
                  )}
                </div>
              ) : (
                <span className={cn("flex items-center gap-1 font-medium", darkMode ? "text-amber-400" : "text-amber-500")}>
                  <Clock className="w-3 h-3" />
                  {log.status === 'retrying' ? 'Retentando' : 'Pendente'}
                </span>
              )}
            </div>

            {/* Duration */}
            <div className="w-14 text-right">
              <span className={cn("font-mono text-[11px]", darkMode ? "text-gray-500" : "text-gray-400")}>
                {formatDuration(log.duration_ms)}
              </span>
            </div>
          </div>
        );
      })}

      {/* Footer */}
      {logs.length > 15 && (
        <div className={cn(
          "px-3 py-2 text-center border-t",
          darkMode ? "border-gray-700 bg-gray-800/40" : "border-gray-100 bg-gray-50"
        )}>
          {onViewAll ? (
            <button
              onClick={onViewAll}
              className={cn("text-xs font-medium transition-colors", darkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700")}
            >
              Ver todos ({logs.length} registros)
            </button>
          ) : (
            <span className={cn("text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>
              Mostrando 15 de {logs.length} registros
            </span>
          )}
        </div>
      )}
    </div>
  );
};
